import Fuse from "fuse.js";
import { useMemo } from "react";

/**
 * You must ensure the `data` and `searchKeys` are both provided as stable
 * references, otherwise fuse will rebuild on every re-render.
 */
export const useFuseSearch = <T>(
  data: T[] | undefined,
  searchKeys: string[],
  searchTerm: string,
  options?: {
    enabled?: boolean;
    threshold?: number; // fuse threshold setting, lower is stricter
    maxResults?: number; // the maximum number of results to return
  },
): T[] => {
  const enabled = options?.enabled ?? true;

  const fuse = useMemo(() => {
    // no need to build the fuse instance if there is no data to search, which
    // can happen when the dataset is still being loaded.
    if (!enabled || !data || data.length === 0) {
      return undefined;
    }

    return new Fuse(data, {
      threshold: options?.threshold ?? 0.1, // 0.1 is a stricter search
      keys: searchKeys,
      ignoreLocation: true,
    });
  }, [enabled, data, searchKeys, options?.threshold]);

  return useMemo(() => {
    // if there is no data to search, just return the same empty data array
    if (!data) {
      return EMPTY as T[];
    }

    if (!enabled) {
      return options?.maxResults !== undefined ?
          data.slice(0, options.maxResults)
        : data;
    }

    if (!fuse) {
      return EMPTY as T[];
    }

    const results =
      searchTerm ? fuse.search(searchTerm).map((result) => result.item) : data;

    const limitedResults =
      options?.maxResults !== undefined ?
        results.slice(0, options.maxResults)
      : results;

    return limitedResults;
  }, [enabled, data, fuse, searchTerm, options?.maxResults]);
};

// return a stable reference
const EMPTY = [];
